'use client';

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';

export default function ActivatePageContent() {
    const { user, getIdToken, isActive } = useAuth();
    const navigate = useNavigate();
    const [code, setCode] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Redirect to dashboard if user is already activated
    useEffect(() => {
        if (user && isActive) {
            navigate('/dashboard');
        }
    }, [user, isActive, navigate]);

    const handleActivate = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!user || isSubmitting) return;

        try {
            setIsSubmitting(true);
            setError(null);

            const token = await getIdToken();
            const response = await fetch('/api/v1/profile/activate', {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ code })
            });

            if (!response.ok) {
                const data = await response.json();
                throw new Error(data.error || 'Failed to activate account');
            }

            // Use Next.js router for navigation instead of direct window.location
            // This is safer for static site generation
            navigate('/dashboard');
        } catch (error: any) {
            setError(error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (!user) {
        navigate('/signin');
        return null;
    }

    return (
        <div className="min-h-screen bg-gray-100 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
            <div className="sm:mx-auto sm:w-full sm:max-w-md">
                <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
                    Activate Your Account
                </h2>
                <div className="mt-8 bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
                    <div className="space-y-6">
                        <div>
                            <p className="text-sm text-gray-500 mb-6">
                                Welcome to DoveText Beta! You&apos;re currently on our waitlist.
                                Enter your invitation code below to activate your account, or wait
                                for your turn to try our service.
                            </p>
                            <p className="text-sm text-gray-500 mb-6">
                                Beta users get priority access and special perks when we launch.
                            </p>
                        </div>

                        <form onSubmit={handleActivate} className="space-y-6">
                            <div>
                                <label htmlFor="code" className="block text-sm font-medium text-gray-700">
                                    Invitation Code
                                </label>
                                <div className="mt-1">
                                    <input
                                        id="code"
                                        name="code"
                                        type="text"
                                        value={code}
                                        onChange={(e) => setCode(e.target.value)}
                                        required
                                        className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                        placeholder="Enter your invitation code"
                                    />
                                </div>
                            </div>

                            {error && (
                                <div className="rounded-md bg-red-50 p-4">
                                    <div className="flex">
                                        <div className="ml-3">
                                            <h3 className="text-sm font-medium text-red-800">
                                                {error}
                                            </h3>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div>
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50"
                                >
                                    {isSubmitting ? 'Activating...' : 'Activate Account'}
                                </button>
                            </div>
                        </form>

                        <div className="mt-6">
                            <div className="relative">
                                <div className="absolute inset-0 flex items-center">
                                    <div className="w-full border-t border-gray-300" />
                                </div>
                                <div className="relative flex justify-center text-sm">
                                    <span className="px-2 bg-white text-gray-500">
                                        Don&apos;t have a code?
                                    </span>
                                </div>
                            </div>

                            <div className="mt-6">
                                <p className="text-center text-sm text-gray-500">
                                    You&apos;re on our waitlist! We&apos;ll notify you when it&apos;s your turn to try DoveText.
                                    <br />
                                    <span className="font-medium">
                                        Current estimated wait time: 2-3 weeks
                                    </span>
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
